import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CloudRain } from "lucide-react";
import {
  fetchPlaceWeather,
  WeatherData,
  WeatherForecastDay,
  WeatherCurrent,
} from "../services/weatherService";
import WeatherIcon from "./WeatherIcon";

interface PlaceWeatherSectionProps {
  placeId: string;
  placeName: string;
  onReadyChange?: (isReady: boolean) => void;
}

const persianNumber = (value: number, maximumFractionDigits = 0) =>
  new Intl.NumberFormat("fa-IR", { maximumFractionDigits }).format(value);

const formatForecastDate = (date: string) =>
  new Intl.DateTimeFormat("fa-IR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(`${date}T12:00:00`));

const ForecastCard: React.FC<{ day: WeatherForecastDay }> = ({ day }) => {
  const precipitation = Math.round((day.precipitation_probability_max || 0) * 100);

  return (
    <article className="min-w-[96px] snap-start rounded-lg border border-gray-200 bg-white p-1 text-center">
      <time className="text-[11px] text-gray-500 font-myYekanFaNumMedium" dateTime={day.date}>
        {formatForecastDate(day.date)}
      </time>
      <div className="mt-2 flex flex-col items-center justify-center gap-1">
        <WeatherIcon condition={day.condition} className="h-9 w-9 shrink-0" loading="lazy" />
        <span className="text-sm text-[#222222] font-myYekanFaNumDemiBold" dir="ltr">
          {persianNumber(day.temperature_max_c)}° / {persianNumber(day.temperature_min_c)}°
        </span>
      </div>
      {/* <p className="mt-1 line-clamp-1 text-[10px] text-gray-600">{day.condition.description}</p>
      <span className="mt-2 flex items-center gap-1 text-[10px] text-gray-500">
        <CloudRain size={12} className="text-[#038545]" aria-hidden="true" />
        احتمال بارش {persianNumber(precipitation)}٪
      </span> */}
    </article>
  );
};

const CurrentWeatherCard: React.FC<{ current: WeatherCurrent }> = ({ current }) => (
  <article className="min-w-[96px] snap-start rounded-lg border border-[#97d2b5] bg-[#f3fefa] p-1 text-center">
    <p className="text-[11px] font-myYekanFaNumMedium text-[#068646]">امروز</p>
    <div className="mt-2 flex flex-col items-center justify-center gap-1">
      <WeatherIcon condition={current.condition} className="h-9 w-9 shrink-0" />
      <span className="text-sm font-myYekanFaNumDemiBold text-[#222222]" dir="ltr">
        {persianNumber(current.temperature_c)}°
      </span>
    </div>
  </article>
);

const PlaceWeatherSection: React.FC<PlaceWeatherSectionProps> = ({ placeId, placeName, onReadyChange }) => {
  const { data } = useQuery<WeatherData, Error>({
    queryKey: ["weather", "place", placeId, 5],
    queryFn: ({ signal }) => fetchPlaceWeather(placeId, 5, signal),
    staleTime: 15 * 60 * 1000,
    retry: false,
    enabled: !!placeId,
  });

  const current = data?.current;
  const currentDate = current?.observed_at?.slice(0, 10);
  const forecastDays = (data?.forecast?.days || [])
    .filter((day) => day.date !== currentDate)
    .slice(0, 5);
  const isReady = Boolean(data && (current || forecastDays.length));

  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);

  if (!isReady || !data) return null;

  return (
    <section className="rounded-xl bg-transparent p-4 text-right font-myYekanFaNumRegular tablet:px-0 tablet:py-6" dir="rtl">
      <h2 className="mb-4 hidden text-[1.09rem] font-myYekanFaNumExtraBold text-[#222222] tablet:block">
        آب‌وهوای {placeName}
      </h2>

      {/* {current && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
          <div className="flex min-w-0 items-center gap-3">
            <WeatherIcon condition={current.condition} className="h-12 w-12 shrink-0" />
            <div className="min-w-0">
              <span className="text-xl text-[#222222] font-myYekanFaNumDemiBold">
                {persianNumber(current.temperature_c, 1)}°
              </span>
              <p className="line-clamp-1 text-xs text-gray-600">{current.condition.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-gray-600">
            <span>احساسی {persianNumber(current.feels_like_c, 1)}°</span>
            <span className="flex items-center gap-1"><Droplets size={13} aria-hidden="true" /> {persianNumber(current.humidity_percent)}٪</span>
          </div>
        </div>
      )} */}

      {(current || forecastDays.length > 0) ? (
        <div className={`${current ? "mt-4" : ""} hide-scrollbar flex snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain pb-1`}>
          {current && <CurrentWeatherCard current={current} />}
          {forecastDays.map((day) => <ForecastCard key={day.date} day={day} />)}
        </div>
      ) : (
        <p className={`${current ? "mt-4" : ""} text-xs text-gray-600`}>پیش‌بینی روزهای آینده فعلاً در دسترس نیست.</p>
      )}

      {!!data.source.warnings?.length && (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[10px] leading-5 text-amber-800">
          {data.source.warnings.map((warning, index) => <p key={`${warning}-${index}`}>{warning}</p>)}
        </div>
      )}
    </section>
  );
};

export default PlaceWeatherSection;
